import React, { useState, useEffect } from 'react'
import PageHeader from '../../../PageHeader/PageHeader'
import { HitApi } from '../../../Api/ApiHit'
import { searchBill, searchCashTransaction, updateCashTransaction } from '../../../constant/Constant'
import { getSelectedUnit } from '../../../storage/Storage'
import { Calendar, RefreshCw, CreditCard, Smartphone, Banknote, Wallet, TrendingDown, ArrowDownCircle, TrendingUp, ArrowUpCircle, Edit2, Check, X } from 'lucide-react'
import toast from 'react-hot-toast'

function PaymentDistributionReport() {
    const [selectedFilter, setSelectedFilter] = useState('today')
    const [showCustomRange, setShowCustomRange] = useState(false)
    const [startDate, setStartDate] = useState('')
    const [endDate, setEndDate] = useState('')
    const [billsData, setBillsData] = useState([])
    const [expenseData, setExpenseData] = useState([])
    const [cashInData, setCashInData] = useState([])
    const [isLoading, setIsLoading] = useState(false)
    const [isLoadingExpenses, setIsLoadingExpenses] = useState(false)
    const [isLoadingCashIn, setIsLoadingCashIn] = useState(false)
    const [editingTransactionId, setEditingTransactionId] = useState(null)
    const [updatingTransactionId, setUpdatingTransactionId] = useState(null)
    const [selectedRowId, setSelectedRowId] = useState(null)
    const [changeReturnFilter, setChangeReturnFilter] = useState('all') // 'all', 'only', 'exclude'
    const [selectedCashInCategory, setSelectedCashInCategory] = useState(null)
    const [selectedExpenseCategory, setSelectedExpenseCategory] = useState(null)

    const filterOptions = [
        { label: 'Today', value: 'today' },
        { label: 'Yesterday', value: 'yesterday' },
        { label: 'This Week', value: 'thisWeek' },
        { label: 'This Month', value: 'thisMonth' },
        { label: 'Last 7 Days', value: 'last7Days' },
        { label: 'Last 30 Days', value: 'last30Days' },
        { label: 'Custom Range', value: 'customRange' },
    ]

    // Category options for cash transactions
    const cashInCategories = [
        { label: 'Deposit', value: 'deposit' },
        { label: 'Refund', value: 'refund' },
        { label: 'Loan Received', value: 'loan_received' },
        { label: 'Investment', value: 'investment' },
        { label: 'Sales Revenue', value: 'sales_revenue' },
        { label: 'Void Entries', value: 'void_entries' },
        { label: 'Other Income', value: 'other_income' },
    ]

    const cashOutCategories = [
        { label: 'Salary', value: 'salary' },
        { label: 'Incentive', value: 'incentive' },
        { label: 'Rent', value: 'rent' },
        { label: 'Utilities', value: 'utilities' },
        { label: 'Supplies', value: 'supplies' },
        { label: 'Maintenance', value: 'maintenance' },
        { label: 'Marketing', value: 'marketing' },
        { label: 'Transport', value: 'transport' },
        { label: 'Food & Beverages', value: 'food_beverages' },
        { label: 'Equipment', value: 'equipment' },
        { label: 'Professional Fees', value: 'professional_fees' },
        { label: 'Loan Repayment', value: 'loan_repayment' },
        { label: 'Send in Bank', value: 'send_in_bank' },
        { label: 'Product', value: 'product' },
        { label: 'Void Entries', value: 'void_entries' },
        { label: 'Expense', value: 'expense' },
        { label: 'Tip', value: 'tip' },
        { label: 'Other Expense', value: 'other_expense' },
    ]

    const handleFilterChange = (value) => {
        setSelectedFilter(value)
        if (value === 'customRange') {
            setShowCustomRange(true)
        } else {
            setShowCustomRange(false)
            setStartDate('')
            setEndDate('')
        }
    }

    const handleApplyCustomRange = () => {
        if (startDate && endDate) {
            console.log('Custom range applied:', startDate, 'to', endDate)
            fetchBillsData()
            fetchExpenseTransactions()
            fetchCashInTransactions()
        }
    }

    // Get timestamps for the selected filter
    const getDateRangeTimestamps = () => {
        const now = new Date()
        let start, end

        switch (selectedFilter) {
            case 'today':
                start = new Date(now).setHours(0, 0, 0, 0)
                end = new Date(now).setHours(23, 59, 59, 999)
                break

            case 'yesterday':
                const yesterday = new Date(now)
                yesterday.setDate(now.getDate() - 1)
                start = yesterday.setHours(0, 0, 0, 0)
                end = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1).setHours(23, 59, 59, 999)
                break

            case 'thisWeek':
                const weekStart = new Date(now)
                const day = now.getDay()
                const diff = now.getDate() - day + (day === 0 ? -6 : 1)
                weekStart.setDate(diff)
                start = weekStart.setHours(0, 0, 0, 0)
                end = new Date(now).setHours(23, 59, 59, 999)
                break

            case 'thisMonth':
                start = new Date(now.getFullYear(), now.getMonth(), 1).setHours(0, 0, 0, 0)
                end = new Date(now).setHours(23, 59, 59, 999)
                break

            case 'last7Days':
                const last7 = new Date(now)
                last7.setDate(now.getDate() - 7)
                start = last7.setHours(0, 0, 0, 0)
                end = new Date(now).setHours(23, 59, 59, 999)
                break

            case 'last30Days':
                const last30 = new Date(now)
                last30.setDate(now.getDate() - 30)
                start = last30.setHours(0, 0, 0, 0)
                end = new Date(now).setHours(23, 59, 59, 999)
                break

            case 'customRange':
                if (startDate && endDate) {
                    start = new Date(startDate).setHours(0, 0, 0, 0)
                    end = new Date(endDate).setHours(23, 59, 59, 999)
                } else {
                    start = new Date(now).setHours(0, 0, 0, 0)
                    end = new Date(now).setHours(23, 59, 59, 999)
                }
                break

            default:
                start = new Date(now).setHours(0, 0, 0, 0)
                end = new Date(now).setHours(23, 59, 59, 999)
        }

        return { start, end }
    }

    // Fetch bills data based on selected filter
    const fetchBillsData = async () => {
        setIsLoading(true)

        try {
            const { start, end } = getDateRangeTimestamps()

            const searchObj = {
                createdAt: {
                    $gte: start,
                    $lte: end
                }
            }

            const json = {
                page: 1,
                limit: 10000,
                search: searchObj
            }

            console.log('🔍 Payment Distribution - Fetching bills with:', json)
            console.log('📅 Date Range:', {
                filter: selectedFilter,
                startDate: new Date(start).toLocaleString(),
                endDate: new Date(end).toLocaleString()
            })

            const res = await HitApi(json, searchBill)

            console.log('✅ Payment Distribution - API Response:', res)

            if (res?.statusCode === 200) {
                const bills = Array.isArray(res?.data?.docs) ? res?.data?.docs : []
                setBillsData(bills)

                console.log('📊 Payment Distribution - Bills loaded:', {
                    totalBills: bills.length,
                    firstBill: bills[0],
                    paymentMethods: bills.map(b => b.payment?.activePaymentMethods).filter(Boolean)
                })

                toast.success(`Loaded ${bills.length} bills`)
            } else {
                setBillsData([])
                console.error('❌ Payment Distribution - Failed to fetch bills:', res)
                toast.error('Failed to fetch bills')
            }
        } catch (error) {
            console.error('❌ Payment Distribution - Error:', error)
            toast.error('Error loading payment data')
            setBillsData([])
        } finally {
            setIsLoading(false)
        }
    }

    // Fetch expense transactions based on selected filter
    const fetchExpenseTransactions = async () => {
        const selectedUnit = getSelectedUnit()
        if (!selectedUnit?._id) {
            console.warn('No unit selected, cannot fetch expense transactions')
            return
        }

        setIsLoadingExpenses(true)

        try {
            const { start, end } = getDateRangeTimestamps()

            const searchObj = {
                unitId: selectedUnit._id,
                transactionType: 'out', // Only cash out transactions (expenses)
                createdAt: {
                    $gte: start,
                    $lte: end
                }
            }

            const json = {
                page: 1,
                limit: 1000,
                search: searchObj,
                sort: { createdAt: -1 }
            }

            console.log('🔍 Fetching expense transactions with:', json)

            const res = await HitApi(json, searchCashTransaction)

            console.log('✅ Expense Transactions - API Response:', res)

            if (res?.statusCode === 200) {
                const expenses = Array.isArray(res?.data?.docs) ? res?.data?.docs : []
                setExpenseData(expenses)

                console.log('📊 Expense transactions loaded:', {
                    totalExpenses: expenses.length,
                    totalAmount: expenses.reduce((sum, exp) => sum + (exp.totalAmount || 0), 0)
                })
            } else {
                setExpenseData([])
                console.error('❌ Failed to fetch expense transactions:', res)
            }
        } catch (error) {
            console.error('❌ Error fetching expense transactions:', error)
            setExpenseData([])
        } finally {
            setIsLoadingExpenses(false)
        }
    }

    // Fetch cash in transactions based on selected filter
    const fetchCashInTransactions = async () => {
        const selectedUnit = getSelectedUnit()
        if (!selectedUnit?._id) {
            console.warn('No unit selected, cannot fetch cash in transactions')
            return
        }

        setIsLoadingCashIn(true)

        try {
            const { start, end } = getDateRangeTimestamps()

            const searchObj = {
                unitId: selectedUnit._id,
                transactionType: 'in', // Only cash in transactions
                createdAt: {
                    $gte: start,
                    $lte: end
                }
            }

            const json = {
                page: 1,
                limit: 1000,
                search: searchObj,
                sort: { createdAt: -1 }
            }

            console.log('🔍 Fetching cash in transactions with:', json)

            const res = await HitApi(json, searchCashTransaction)

            console.log('✅ Cash In Transactions - API Response:', res)

            if (res?.statusCode === 200) {
                const cashInTransactions = Array.isArray(res?.data?.docs) ? res?.data?.docs : []
                setCashInData(cashInTransactions)

                console.log('📊 Cash in transactions loaded:', {
                    totalTransactions: cashInTransactions.length,
                    totalAmount: cashInTransactions.reduce((sum, tx) => sum + (tx.totalAmount || 0), 0)
                })
            } else {
                setCashInData([])
                console.error('❌ Failed to fetch cash in transactions:', res)
            }
        } catch (error) {
            console.error('❌ Error fetching cash in transactions:', error)
            setCashInData([])
        } finally {
            setIsLoadingCashIn(false)
        }
    }

    // Get display text for date range
    const getDateRangeText = () => {
        const now = new Date()

        switch (selectedFilter) {
            case 'today':
                return now.toLocaleDateString('en-US', {
                    weekday: 'long',
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                })

            case 'yesterday':
                const yesterday = new Date(now)
                yesterday.setDate(now.getDate() - 1)
                return yesterday.toLocaleDateString('en-US', {
                    weekday: 'long',
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                })

            case 'thisWeek':
                const weekStart = new Date(now)
                const day = now.getDay()
                const diff = now.getDate() - day + (day === 0 ? -6 : 1)
                weekStart.setDate(diff)
                return `${weekStart.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - ${now.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`

            case 'thisMonth':
                const monthStart = new Date(now.getFullYear(), now.getMonth(), 1)
                return `${monthStart.toLocaleDateString('en-US', { month: 'long', day: 'numeric' })} - ${now.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}`

            case 'last7Days':
                const last7 = new Date(now)
                last7.setDate(now.getDate() - 7)
                return `${last7.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - ${now.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`

            case 'last30Days':
                const last30 = new Date(now)
                last30.setDate(now.getDate() - 30)
                return `${last30.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - ${now.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`

            case 'customRange':
                if (startDate && endDate) {
                    const start = new Date(startDate)
                    const end = new Date(endDate)
                    return `${start.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })} - ${end.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`
                }
                return 'Select custom date range'

            default:
                return ''
        }
    }

    // Fetch data when filter changes
    useEffect(() => {
        if (selectedFilter !== 'customRange') {
            fetchBillsData()
            fetchExpenseTransactions()
            fetchCashInTransactions()
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedFilter])

    // Initial load
    useEffect(() => {
        fetchBillsData()
        fetchExpenseTransactions()
        fetchCashInTransactions()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    // Calculate payment distribution
    const calculatePaymentDistribution = () => {
        const distribution = {
            cash: 0,
            card: 0,
            upi: 0,
            wallet: 0,
            other: 0,
            totalAmount: 0,
            totalBills: billsData.length
        }

        billsData.forEach(bill => {
            const paymentMethods = bill.payment?.activePaymentMethods || []
            const billTotal = bill.calculations?.totals?.finalAmount || 0

            // If bill has multiple payment methods, we need to distribute the amount
            if (paymentMethods.length > 0) {
                paymentMethods.forEach(paymentMethod => {
                    const method = paymentMethod.method?.toLowerCase() || ''
                    const amount = paymentMethod.amount || 0

                    if (method.includes('cash')) {
                        distribution.cash += amount
                    } else if (method.includes('card')) {
                        distribution.card += amount
                    } else if (method.includes('upi') || method.includes('online')) {
                        distribution.upi += amount
                    } else if (method.includes('wallet')) {
                        distribution.wallet += amount
                    } else {
                        distribution.other += amount
                    }

                    distribution.totalAmount += amount
                })
            } else {
                // If no payment methods specified, count as cash by default
                distribution.cash += billTotal
                distribution.totalAmount += billTotal
            }
        })

        return distribution
    }

    const paymentStats = calculatePaymentDistribution()

    // Calculate expense statistics
    const calculateExpenseStats = () => {
        const stats = {
            totalExpenses: expenseData.length,
            totalAmount: 0,
            byCategory: {}
        }

        expenseData.forEach(expense => {
            const amount = expense.totalAmount || 0
            const category = expense.category || 'Other'

            stats.totalAmount += amount

            if (!stats.byCategory[category]) {
                stats.byCategory[category] = {
                    count: 0,
                    amount: 0
                }
            }

            stats.byCategory[category].count += 1
            stats.byCategory[category].amount += amount
        })

        return stats
    }

    const expenseStats = calculateExpenseStats()

    // Calculate cash in statistics
    const calculateCashInStats = () => {
        const stats = {
            totalTransactions: cashInData.length,
            totalAmount: 0,
            byCategory: {}
        }

        cashInData.forEach(cashIn => {
            const amount = cashIn.totalAmount || 0
            const category = cashIn.category || 'Other'

            stats.totalAmount += amount

            if (!stats.byCategory[category]) {
                stats.byCategory[category] = {
                    count: 0,
                    amount: 0
                }
            }

            stats.byCategory[category].count += 1
            stats.byCategory[category].amount += amount
        })

        return stats
    }

    const cashInStats = calculateCashInStats()

    // Filter transactions based on "Change returned" text
    const isChangeReturnTransaction = (description) => {
        return description && description.toLowerCase().includes('change returned')
    }

    const filterTransactions = (transactions, categoryFilter = null) => {
        let filtered = transactions

        // Filter by change return status
        if (changeReturnFilter === 'only') {
            filtered = filtered.filter(t => isChangeReturnTransaction(t.description))
        } else if (changeReturnFilter === 'exclude') {
            filtered = filtered.filter(t => !isChangeReturnTransaction(t.description))
        }

        // Filter by category
        if (categoryFilter) {
            filtered = filtered.filter(t => t.category === categoryFilter)
        }

        return filtered
    }

    const filteredCashInData = filterTransactions(cashInData, selectedCashInCategory)
    const filteredExpenseData = filterTransactions(expenseData, selectedExpenseCategory)

    // Count change return transactions
    const changeReturnCounts = {
        cashIn: cashInData.filter(t => isChangeReturnTransaction(t.description)).length,
        cashOut: expenseData.filter(t => isChangeReturnTransaction(t.description)).length
    }

    // Calculate change return statistics
    const calculateChangeReturnStats = () => {
        const cashInChangeReturns = cashInData.filter(t => isChangeReturnTransaction(t.description))
        const cashOutChangeReturns = expenseData.filter(t => isChangeReturnTransaction(t.description))

        const cashInTotal = cashInChangeReturns.reduce((sum, t) => sum + (t.totalAmount || 0), 0)
        const cashOutTotal = cashOutChangeReturns.reduce((sum, t) => sum + (t.totalAmount || 0), 0)

        return {
            cashIn: {
                count: cashInChangeReturns.length,
                total: cashInTotal,
                transactions: cashInChangeReturns
            },
            cashOut: {
                count: cashOutChangeReturns.length,
                total: cashOutTotal,
                transactions: cashOutChangeReturns
            },
            totalCount: cashInChangeReturns.length + cashOutChangeReturns.length,
            netAmount: cashInTotal - cashOutTotal,
            totalIn: cashInTotal,
            totalOut: cashOutTotal
        }
    }

    const changeReturnStats = calculateChangeReturnStats()

    // Format currency
    const formatCurrency = (amount) => {
        if (amount === null || amount === undefined) return '₹0'
        return `₹${Number(amount).toLocaleString('en-IN', { maximumFractionDigits: 2, minimumFractionDigits: 2 })}`
    }

    // Calculate percentage
    const calculatePercentage = (amount) => {
        if (paymentStats.totalAmount === 0) return 0
        return ((amount / paymentStats.totalAmount) * 100).toFixed(1)
    }

    // Handle keyboard navigation
    const handleRowKeyPress = (event, transactionId, transactionType) => {
        // Don't handle arrow keys if we're in edit mode (let dropdown handle them)
        if (editingTransactionId === transactionId && (event.key === 'ArrowUp' || event.key === 'ArrowDown')) {
            return
        }

        if (event.key === 'Enter') {
            event.preventDefault()
            if (editingTransactionId === transactionId) {
                // If already editing, do nothing (let dropdown handle it)
                return
            }
            // Enter edit mode
            setEditingTransactionId(transactionId)
            setSelectedRowId(transactionId)
        } else if (event.key === 'Escape') {
            event.preventDefault()
            // Exit edit mode
            setEditingTransactionId(null)
            setSelectedRowId(null)
        } else if (event.key === 'ArrowDown') {
            event.preventDefault()
            // Move to next row
            const dataArray = transactionType === 'in' ? cashInData : expenseData
            const currentIndex = dataArray.findIndex(t => t._id === transactionId)
            if (currentIndex < dataArray.length - 1) {
                const nextTransaction = dataArray[currentIndex + 1]
                setSelectedRowId(nextTransaction._id)
                // Focus the next row
                setTimeout(() => {
                    const nextRow = document.querySelector(`[data-transaction-id="${nextTransaction._id}"]`)
                    if (nextRow) nextRow.focus()
                }, 0)
            }
        } else if (event.key === 'ArrowUp') {
            event.preventDefault()
            // Move to previous row
            const dataArray = transactionType === 'in' ? cashInData : expenseData
            const currentIndex = dataArray.findIndex(t => t._id === transactionId)
            if (currentIndex > 0) {
                const prevTransaction = dataArray[currentIndex - 1]
                setSelectedRowId(prevTransaction._id)
                // Focus the previous row
                setTimeout(() => {
                    const prevRow = document.querySelector(`[data-transaction-id="${prevTransaction._id}"]`)
                    if (prevRow) prevRow.focus()
                }, 0)
            }
        }
    }

    // Update transaction category
    const updateTransactionCategory = async (transactionId, newCategory, transactionType) => {
        setUpdatingTransactionId(transactionId)

        try {
            const updateData = {
                _id: transactionId,
                category: newCategory
            }

            console.log('🔄 Updating transaction category:', updateData)

            const res = await HitApi(updateData, updateCashTransaction)

            if (res?.statusCode === 200) {
                // Update local state based on transaction type
                if (transactionType === 'in') {
                    setCashInData(prev =>
                        prev.map(tx =>
                            tx._id === transactionId ? { ...tx, category: newCategory } : tx
                        )
                    )
                } else {
                    setExpenseData(prev =>
                        prev.map(tx =>
                            tx._id === transactionId ? { ...tx, category: newCategory } : tx
                        )
                    )
                }

                toast.success('Category updated successfully')
                setEditingTransactionId(null)
            } else {
                toast.error('Failed to update category')
                console.error('Update failed:', res)
            }
        } catch (error) {
            toast.error('Error updating category')
            console.error('Error updating transaction:', error)
        } finally {
            setUpdatingTransactionId(null)
        }
    }

    return (
        <div className='p-3 sm:p-4 md:p-5'>
            <PageHeader
                title={'Payment Distribution Report'}
                description={'View payment distribution across different payment methods'}
            />

            {/* Header Section with Filter */}
            <div className="bg-white rounded-2xl shadow-xl p-6 mb-6">
                <div className="flex items-start justify-between">
                    <div className="flex-1">
                        <div className="flex items-center gap-2 mt-3 bg-blue-50 px-4 py-2 rounded-lg border border-blue-100">
                            <Calendar className="w-4 h-4 text-blue-600" />
                            <span className="font-medium text-blue-700 text-sm">{getDateRangeText()}</span>
                        </div>
                    </div>

                    {/* Filter Dropdown and Refresh Button */}
                    <div className="flex items-center gap-2">
                        <button
                            onClick={() => {
                                fetchBillsData()
                                fetchExpenseTransactions()
                                fetchCashInTransactions()
                                toast.success('Data refreshed')
                            }}
                            disabled={isLoading || isLoadingExpenses || isLoadingCashIn}
                            className="p-2.5 border-2 border-gray-200 rounded-xl bg-white text-gray-700 hover:bg-blue-50 hover:border-blue-300 transition-all shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
                            title="Refresh data"
                        >
                            <RefreshCw className={`w-5 h-5 ${(isLoading || isLoadingExpenses || isLoadingCashIn) ? 'animate-spin' : ''}`} />
                        </button>
                        <select
                            value={selectedFilter}
                            onChange={(e) => handleFilterChange(e.target.value)}
                            className="px-4 py-2.5 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white text-gray-700 font-medium cursor-pointer shadow-sm hover:border-blue-300 transition-all"
                        >
                            {filterOptions.map((option) => (
                                <option key={option.value} value={option.value}>
                                    {option.label}
                                </option>
                            ))}
                        </select>
                    </div>
                </div>

                {/* Custom Date Range Picker */}
                {showCustomRange && (
                    <div className="bg-gradient-to-r from-blue-50 to-purple-50 border-2 border-blue-200 rounded-xl p-4 mt-4 shadow-inner">
                        <div className="flex items-center gap-4 flex-wrap">
                            <div className="flex items-center gap-2">
                                <Calendar className="w-5 h-5 text-blue-600" />
                                <span className="text-sm font-semibold text-gray-700">Custom Date Range:</span>
                            </div>

                            <div className="flex items-center gap-2">
                                <label className="text-sm text-gray-600 font-medium">From:</label>
                                <input
                                    type="date"
                                    value={startDate}
                                    onChange={(e) => setStartDate(e.target.value)}
                                    className="px-3 py-2 border-2 border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white shadow-sm"
                                />
                            </div>

                            <div className="flex items-center gap-2">
                                <label className="text-sm text-gray-600 font-medium">To:</label>
                                <input
                                    type="date"
                                    value={endDate}
                                    onChange={(e) => setEndDate(e.target.value)}
                                    className="px-3 py-2 border-2 border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white shadow-sm"
                                />
                            </div>

                            <button
                                onClick={handleApplyCustomRange}
                                disabled={!startDate || !endDate}
                                className={`px-6 py-2 rounded-lg font-semibold transition-all shadow-md ${
                                    startDate && endDate
                                        ? 'bg-gradient-to-r from-blue-600 to-purple-600 text-white hover:shadow-lg transform hover:-translate-y-0.5'
                                        : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                                }`}
                            >
                                Apply Filter
                            </button>
                        </div>
                    </div>
                )}
            </div>

            {/* Loading State */}
            {(isLoading || isLoadingExpenses || isLoadingCashIn) && (
                <div className="flex flex-col justify-center items-center py-20">
                    <div className="relative">
                        <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-blue-600"></div>
                        <div className="absolute top-0 left-0 animate-ping rounded-full h-16 w-16 border-b-4 border-purple-400 opacity-75"></div>
                    </div>
                    <p className="mt-4 text-gray-600 font-medium">
                        Loading financial data...
                    </p>
                </div>
            )}

            {/* Content Section */}
            {!isLoading && (
                <>
                    {/* Summary Stats */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                        <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl shadow-lg p-6 text-white">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-blue-100 text-sm font-medium mb-1">Total Bills</p>
                                    <p className="text-4xl font-bold">{paymentStats.totalBills}</p>
                                </div>
                                <div className="bg-white bg-opacity-20 rounded-full p-4">
                                    <Calendar className="w-8 h-8" />
                                </div>
                            </div>
                        </div>

                        <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-xl shadow-lg p-6 text-white">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-green-100 text-sm font-medium mb-1">Total Revenue</p>
                                    <p className="text-4xl font-bold">{formatCurrency(paymentStats.totalAmount)}</p>
                                </div>
                                <div className="bg-white bg-opacity-20 rounded-full p-4">
                                    <Banknote className="w-8 h-8" />
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Payment Distribution Cards */}
                    <div className="bg-white rounded-xl shadow-lg p-6 mb-6 border border-gray-200">
                        <h2 className="text-2xl font-bold text-gray-900 mb-6">Payment Distribution</h2>

                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                            {/* Cash Card */}
                            <div className="bg-gradient-to-br from-green-50 to-green-100 border-2 border-green-200 rounded-xl p-6 hover:shadow-lg transition-all">
                                <div className="flex items-center justify-between mb-4">
                                    <div className="bg-green-500 rounded-full p-3">
                                        <Banknote className="w-6 h-6 text-white" />
                                    </div>
                                    <span className="text-sm font-bold text-green-700 bg-green-200 px-3 py-1 rounded-full">
                                        {calculatePercentage(paymentStats.cash)}%
                                    </span>
                                </div>
                                <h3 className="text-gray-600 text-sm font-medium mb-2">Cash</h3>
                                <p className="text-3xl font-bold text-green-700">{formatCurrency(paymentStats.cash)}</p>
                            </div>

                            {/* Card Payment */}
                            <div className="bg-gradient-to-br from-blue-50 to-blue-100 border-2 border-blue-200 rounded-xl p-6 hover:shadow-lg transition-all">
                                <div className="flex items-center justify-between mb-4">
                                    <div className="bg-blue-500 rounded-full p-3">
                                        <CreditCard className="w-6 h-6 text-white" />
                                    </div>
                                    <span className="text-sm font-bold text-blue-700 bg-blue-200 px-3 py-1 rounded-full">
                                        {calculatePercentage(paymentStats.card)}%
                                    </span>
                                </div>
                                <h3 className="text-gray-600 text-sm font-medium mb-2">Card</h3>
                                <p className="text-3xl font-bold text-blue-700">{formatCurrency(paymentStats.card)}</p>
                            </div>

                            {/* UPI Payment */}
                            <div className="bg-gradient-to-br from-purple-50 to-purple-100 border-2 border-purple-200 rounded-xl p-6 hover:shadow-lg transition-all">
                                <div className="flex items-center justify-between mb-4">
                                    <div className="bg-purple-500 rounded-full p-3">
                                        <Smartphone className="w-6 h-6 text-white" />
                                    </div>
                                    <span className="text-sm font-bold text-purple-700 bg-purple-200 px-3 py-1 rounded-full">
                                        {calculatePercentage(paymentStats.upi)}%
                                    </span>
                                </div>
                                <h3 className="text-gray-600 text-sm font-medium mb-2">UPI / Online</h3>
                                <p className="text-3xl font-bold text-purple-700">{formatCurrency(paymentStats.upi)}</p>
                            </div>

                            {/* Wallet Payment */}
                            <div className="bg-gradient-to-br from-orange-50 to-orange-100 border-2 border-orange-200 rounded-xl p-6 hover:shadow-lg transition-all">
                                <div className="flex items-center justify-between mb-4">
                                    <div className="bg-orange-500 rounded-full p-3">
                                        <Wallet className="w-6 h-6 text-white" />
                                    </div>
                                    <span className="text-sm font-bold text-orange-700 bg-orange-200 px-3 py-1 rounded-full">
                                        {calculatePercentage(paymentStats.wallet)}%
                                    </span>
                                </div>
                                <h3 className="text-gray-600 text-sm font-medium mb-2">Wallet</h3>
                                <p className="text-3xl font-bold text-orange-700">{formatCurrency(paymentStats.wallet)}</p>
                            </div>
                        </div>
                    </div>

                    {/* Breakdown Table */}
                    <div className="bg-white rounded-xl shadow-lg p-6 border border-gray-200">
                        <h2 className="text-xl font-bold text-gray-900 mb-4">Payment Method Breakdown</h2>
                        <div className="overflow-x-auto">
                            <table className="min-w-full divide-y divide-gray-200">
                                <thead className="bg-gray-50">
                                    <tr>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Payment Method
                                        </th>
                                        <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Amount
                                        </th>
                                        <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Percentage
                                        </th>
                                    </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-200">
                                    <tr className="hover:bg-green-50">
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <div className="flex items-center">
                                                <div className="bg-green-100 rounded-full p-2 mr-3">
                                                    <Banknote className="w-5 h-5 text-green-600" />
                                                </div>
                                                <span className="text-sm font-medium text-gray-900">Cash</span>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-semibold text-gray-900">
                                            {formatCurrency(paymentStats.cash)}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-right">
                                            <span className="px-3 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">
                                                {calculatePercentage(paymentStats.cash)}%
                                            </span>
                                        </td>
                                    </tr>
                                    <tr className="hover:bg-blue-50">
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <div className="flex items-center">
                                                <div className="bg-blue-100 rounded-full p-2 mr-3">
                                                    <CreditCard className="w-5 h-5 text-blue-600" />
                                                </div>
                                                <span className="text-sm font-medium text-gray-900">Card</span>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-semibold text-gray-900">
                                            {formatCurrency(paymentStats.card)}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-right">
                                            <span className="px-3 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-blue-100 text-blue-800">
                                                {calculatePercentage(paymentStats.card)}%
                                            </span>
                                        </td>
                                    </tr>
                                    <tr className="hover:bg-purple-50">
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <div className="flex items-center">
                                                <div className="bg-purple-100 rounded-full p-2 mr-3">
                                                    <Smartphone className="w-5 h-5 text-purple-600" />
                                                </div>
                                                <span className="text-sm font-medium text-gray-900">UPI / Online</span>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-semibold text-gray-900">
                                            {formatCurrency(paymentStats.upi)}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-right">
                                            <span className="px-3 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-purple-100 text-purple-800">
                                                {calculatePercentage(paymentStats.upi)}%
                                            </span>
                                        </td>
                                    </tr>
                                    <tr className="hover:bg-orange-50">
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <div className="flex items-center">
                                                <div className="bg-orange-100 rounded-full p-2 mr-3">
                                                    <Wallet className="w-5 h-5 text-orange-600" />
                                                </div>
                                                <span className="text-sm font-medium text-gray-900">Wallet</span>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-semibold text-gray-900">
                                            {formatCurrency(paymentStats.wallet)}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-right">
                                            <span className="px-3 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-orange-100 text-orange-800">
                                                {calculatePercentage(paymentStats.wallet)}%
                                            </span>
                                        </td>
                                    </tr>
                                    <tr className="bg-gray-100 font-semibold">
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                            Total
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm text-gray-900">
                                            {formatCurrency(paymentStats.totalAmount)}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-right">
                                            <span className="px-3 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-gray-200 text-gray-800">
                                                100%
                                            </span>
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>

                    {/* Change Returned Card Section */}
                    {changeReturnStats.totalCount > 0 && (
                        <div className="bg-gradient-to-br from-yellow-50 via-amber-50 to-orange-50 rounded-xl shadow-lg p-6 border-2 border-yellow-400 mt-6">
                            {/* Header */}
                            <div className="flex items-center justify-between mb-6">
                                <div className="flex items-center gap-3">
                                    <div className="bg-gradient-to-br from-yellow-400 to-amber-500 rounded-full p-3 shadow-md">
                                        <svg className="w-7 h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                        </svg>
                                    </div>
                                    <div>
                                        <h2 className="text-2xl font-bold text-yellow-900">💰 Change Returned Transactions</h2>
                                        <p className="text-sm text-yellow-700">Tracking all change given back to customers</p>
                                    </div>
                                </div>
                                <div className="text-right">
                                    <p className="text-sm text-yellow-700 font-medium">Total Transactions</p>
                                    <p className="text-4xl font-bold text-yellow-900">{changeReturnStats.totalCount}</p>
                                </div>
                            </div>

                            {/* Summary Cards */}
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                                {/* Cash In Change Returns */}
                                <div className="bg-white rounded-lg shadow-md p-4 border-2 border-green-300">
                                    <div className="flex items-center justify-between mb-2">
                                        <h3 className="text-sm font-semibold text-gray-700">Cash In (Change Given)</h3>
                                        <div className="bg-green-100 rounded-full p-2">
                                            <TrendingUp className="w-4 h-4 text-green-600" />
                                        </div>
                                    </div>
                                    <p className="text-3xl font-bold text-green-600">{formatCurrency(changeReturnStats.cashIn.total)}</p>
                                    <p className="text-xs text-gray-600 mt-1">{changeReturnStats.cashIn.count} transactions</p>
                                </div>

                                {/* Cash Out Change Returns */}
                                <div className="bg-white rounded-lg shadow-md p-4 border-2 border-red-300">
                                    <div className="flex items-center justify-between mb-2">
                                        <h3 className="text-sm font-semibold text-gray-700">Cash Out (Change Returned)</h3>
                                        <div className="bg-red-100 rounded-full p-2">
                                            <TrendingDown className="w-4 h-4 text-red-600" />
                                        </div>
                                    </div>
                                    <p className="text-3xl font-bold text-red-600">{formatCurrency(changeReturnStats.cashOut.total)}</p>
                                    <p className="text-xs text-gray-600 mt-1">{changeReturnStats.cashOut.count} transactions</p>
                                </div>

                                {/* Net Change */}
                                <div className="bg-gradient-to-br from-yellow-100 to-amber-100 rounded-lg shadow-md p-4 border-2 border-yellow-500">
                                    <div className="flex items-center justify-between mb-2">
                                        <h3 className="text-sm font-semibold text-yellow-900">Net Change Flow</h3>
                                        <div className="bg-yellow-200 rounded-full p-2">
                                            <Banknote className="w-4 h-4 text-yellow-700" />
                                        </div>
                                    </div>
                                    <p className={`text-3xl font-bold ${changeReturnStats.netAmount >= 0 ? 'text-green-700' : 'text-red-700'}`}>
                                        {formatCurrency(Math.abs(changeReturnStats.netAmount))}
                                    </p>
                                    <p className="text-xs text-yellow-800 mt-1">
                                        {changeReturnStats.netAmount >= 0 ? '↑ More In' : '↓ More Out'}
                                    </p>
                                </div>
                            </div>

                            {/* Recent Change Return Transactions */}
                            {(changeReturnStats.cashIn.transactions.length > 0 || changeReturnStats.cashOut.transactions.length > 0) && (
                                <div className="bg-white rounded-lg shadow-md p-4 border border-yellow-300">
                                    <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                                        <span>Recent Change Transactions</span>
                                        <span className="text-xs bg-yellow-200 text-yellow-800 px-2 py-1 rounded-full font-semibold">
                                            Last {Math.min(changeReturnStats.totalCount, 10)}
                                        </span>
                                    </h3>

                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        {/* Cash In Changes */}
                                        {changeReturnStats.cashIn.transactions.length > 0 && (
                                            <div>
                                                <h4 className="text-sm font-semibold text-green-700 mb-2 flex items-center gap-2">
                                                    <TrendingUp className="w-4 h-4" />
                                                    Cash In Changes ({changeReturnStats.cashIn.count})
                                                </h4>
                                                <div className="space-y-2 max-h-64 overflow-y-auto">
                                                    {changeReturnStats.cashIn.transactions.slice(0, 5).map((transaction, index) => (
                                                        <div key={transaction._id || index} className="bg-green-50 border border-green-200 rounded-lg p-3">
                                                            <div className="flex justify-between items-start mb-1">
                                                                <span className="text-sm font-bold text-green-700">
                                                                    {formatCurrency(transaction.totalAmount)}
                                                                </span>
                                                                <span className="text-xs text-gray-500">
                                                                    {new Date(transaction.createdAt).toLocaleDateString()}
                                                                </span>
                                                            </div>
                                                            <p className="text-xs text-gray-600 truncate">
                                                                {transaction.description || 'No description'}
                                                            </p>
                                                            {transaction.recordedBy?.name && (
                                                                <p className="text-xs text-gray-500 mt-1">
                                                                    By: {transaction.recordedBy.name}
                                                                </p>
                                                            )}
                                                        </div>
                                                    ))}
                                                    {changeReturnStats.cashIn.count > 5 && (
                                                        <p className="text-xs text-center text-gray-500 italic pt-2">
                                                            +{changeReturnStats.cashIn.count - 5} more in main table
                                                        </p>
                                                    )}
                                                </div>
                                            </div>
                                        )}

                                        {/* Cash Out Changes */}
                                        {changeReturnStats.cashOut.transactions.length > 0 && (
                                            <div>
                                                <h4 className="text-sm font-semibold text-red-700 mb-2 flex items-center gap-2">
                                                    <TrendingDown className="w-4 h-4" />
                                                    Cash Out Changes ({changeReturnStats.cashOut.count})
                                                </h4>
                                                <div className="space-y-2 max-h-64 overflow-y-auto">
                                                    {changeReturnStats.cashOut.transactions.slice(0, 5).map((transaction, index) => (
                                                        <div key={transaction._id || index} className="bg-red-50 border border-red-200 rounded-lg p-3">
                                                            <div className="flex justify-between items-start mb-1">
                                                                <span className="text-sm font-bold text-red-700">
                                                                    {formatCurrency(transaction.totalAmount)}
                                                                </span>
                                                                <span className="text-xs text-gray-500">
                                                                    {new Date(transaction.createdAt).toLocaleDateString()}
                                                                </span>
                                                            </div>
                                                            <p className="text-xs text-gray-600 truncate">
                                                                {transaction.description || 'No description'}
                                                            </p>
                                                            {transaction.inAccountTo?.name && (
                                                                <p className="text-xs text-gray-500 mt-1">
                                                                    To: {transaction.inAccountTo.name}
                                                                </p>
                                                            )}
                                                        </div>
                                                    ))}
                                                    {changeReturnStats.cashOut.count > 5 && (
                                                        <p className="text-xs text-center text-gray-500 italic pt-2">
                                                            +{changeReturnStats.cashOut.count - 5} more in main table
                                                        </p>
                                                    )}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}

                            {/* Quick Actions */}
                            <div className="mt-4 flex items-center justify-between bg-yellow-100 border-2 border-yellow-400 rounded-lg p-3">
                                <p className="text-sm text-yellow-800 font-medium">
                                    💡 Use the filter below to view only change transactions in detailed tables
                                </p>
                                <button
                                    onClick={() => setChangeReturnFilter('only')}
                                    className="px-4 py-2 bg-yellow-500 hover:bg-yellow-600 text-white rounded-lg font-semibold shadow-md transition-colors"
                                >
                                    View All Changes
                                </button>
                            </div>
                        </div>
                    )}

                    {/* Filter for Change Returned Transactions */}
                    {(cashInData.length > 0 || expenseData.length > 0) && (changeReturnCounts.cashIn > 0 || changeReturnCounts.cashOut > 0) && (
                        <div className="bg-gradient-to-r from-yellow-50 to-amber-50 rounded-xl shadow-md p-4 border-2 border-yellow-300 mt-6">
                            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                                <div className="flex items-center gap-3">
                                    <div className="bg-yellow-400 rounded-full p-2">
                                        <svg className="w-5 h-5 text-yellow-900" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                        </svg>
                                    </div>
                                    <div>
                                        <h3 className="text-sm font-bold text-yellow-900">Change Return Transactions</h3>
                                        <p className="text-xs text-yellow-700">
                                            Found {changeReturnCounts.cashIn + changeReturnCounts.cashOut} transactions with "Change returned"
                                            <span className="ml-2">
                                                (Cash In: {changeReturnCounts.cashIn}, Cash Out: {changeReturnCounts.cashOut})
                                            </span>
                                        </p>
                                    </div>
                                </div>
                                <div className="flex items-center gap-2">
                                    <label className="text-xs font-medium text-yellow-900">Filter:</label>
                                    <select
                                        value={changeReturnFilter}
                                        onChange={(e) => setChangeReturnFilter(e.target.value)}
                                        className="px-3 py-2 border-2 border-yellow-400 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500 bg-white text-sm font-medium shadow-sm"
                                    >
                                        <option value="all">Show All</option>
                                        <option value="only">Only Change Returns</option>
                                        <option value="exclude">Exclude Change Returns</option>
                                    </select>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Cash In Transactions Section */}
                    <div className="bg-white rounded-xl shadow-lg p-6 border border-gray-200 mt-6">
                        <div className="flex items-center justify-between mb-6">
                            <div className="flex items-center gap-3">
                                <div className="bg-green-100 rounded-full p-3">
                                    <TrendingUp className="w-6 h-6 text-green-600" />
                                </div>
                                <div>
                                    <div className="flex items-center gap-2">
                                        <h2 className="text-2xl font-bold text-gray-900">Cash In Transactions</h2>
                                        {selectedCashInCategory && (
                                            <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800 border border-green-300">
                                                Filtered: {selectedCashInCategory}
                                                <button
                                                    onClick={() => setSelectedCashInCategory(null)}
                                                    className="ml-1 hover:bg-green-200 rounded-full p-0.5"
                                                    title="Clear filter"
                                                >
                                                    <X className="w-3 h-3" />
                                                </button>
                                            </span>
                                        )}
                                    </div>
                                    <p className="text-sm text-gray-600">
                                        {selectedCashInCategory
                                            ? `Showing ${selectedCashInCategory} transactions only`
                                            : 'All cash deposits and receipts in the selected period'
                                        }
                                    </p>
                                </div>
                            </div>
                            <div className="text-right">
                                <p className="text-sm text-gray-600">Total Cash In</p>
                                <p className="text-3xl font-bold text-green-600">{formatCurrency(cashInStats.totalAmount)}</p>
                                <p className="text-xs text-gray-500 mt-1">{cashInStats.totalTransactions} transactions</p>
                            </div>
                        </div>

                        {/* Cash In Categories Summary */}
                        {Object.keys(cashInStats.byCategory).length > 0 && (
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                                {Object.entries(cashInStats.byCategory).map(([category, data]) => (
                                    <div
                                        key={category}
                                        onClick={() => {
                                            if (selectedCashInCategory === category) {
                                                setSelectedCashInCategory(null)
                                            } else {
                                                setSelectedCashInCategory(category)
                                            }
                                        }}
                                        className={`bg-gradient-to-br from-green-50 to-emerald-50 border rounded-lg p-4 cursor-pointer transition-all hover:shadow-md ${
                                            selectedCashInCategory === category
                                                ? 'border-green-500 border-2 ring-2 ring-green-300 shadow-lg'
                                                : 'border-green-200'
                                        }`}
                                    >
                                        <div className="flex items-center justify-between mb-2">
                                            <h4 className="text-sm font-medium text-gray-700 capitalize">{category}</h4>
                                            <span className="bg-green-100 text-green-800 text-xs font-bold px-2 py-1 rounded">
                                                {data.count}
                                            </span>
                                        </div>
                                        <p className="text-2xl font-bold text-green-700">{formatCurrency(data.amount)}</p>
                                    </div>
                                ))}
                            </div>
                        )}

                        {/* Cash In Transactions Table */}
                        {filteredCashInData.length > 0 ? (
                            <>
                                <div className="mb-3 p-2 bg-blue-50 border border-blue-200 rounded-lg">
                                    <p className="text-xs text-blue-700">
                                        💡 <strong>Keyboard shortcuts:</strong> Use <kbd className="px-1.5 py-0.5 bg-white border border-blue-300 rounded text-blue-800 font-mono">↑</kbd> <kbd className="px-1.5 py-0.5 bg-white border border-blue-300 rounded text-blue-800 font-mono">↓</kbd> to navigate rows, <kbd className="px-1.5 py-0.5 bg-white border border-blue-300 rounded text-blue-800 font-mono">Enter</kbd> to edit category, <kbd className="px-1.5 py-0.5 bg-white border border-blue-300 rounded text-blue-800 font-mono">Esc</kbd> to cancel
                                    </p>
                                </div>
                                <div className="overflow-x-auto">
                                <table className="min-w-full divide-y divide-gray-200">
                                    <thead className="bg-gray-50">
                                        <tr>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                Status
                                            </th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                Date & Time
                                            </th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                Category
                                            </th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                Description
                                            </th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                Recorded By
                                            </th>
                                            <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                Amount
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody className="bg-white divide-y divide-gray-200">
                                        {filteredCashInData.map((cashIn, index) => (
                                            <tr
                                                key={cashIn._id || index}
                                                data-transaction-id={cashIn._id}
                                                className={`hover:bg-green-50 cursor-pointer transition-colors ${selectedRowId === cashIn._id ? 'bg-green-100 ring-2 ring-green-400' : ''} ${isChangeReturnTransaction(cashIn.description) ? 'bg-yellow-50 border-l-4 border-yellow-400' : ''}`}
                                                tabIndex={0}
                                                onKeyDown={(e) => handleRowKeyPress(e, cashIn._id, 'in')}
                                                onClick={() => setSelectedRowId(cashIn._id)}
                                                onFocus={() => setSelectedRowId(cashIn._id)}
                                            >
                                                <td className="px-6 py-4 whitespace-nowrap">
                                                    {isChangeReturnTransaction(cashIn.description) ? (
                                                        <div className="flex items-center gap-1">
                                                            <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-bold bg-yellow-400 text-yellow-900 border-2 border-yellow-500 shadow-sm">
                                                                💰 Change
                                                            </span>
                                                        </div>
                                                    ) : (
                                                        <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-700">
                                                            Normal
                                                        </span>
                                                    )}
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                    <div>
                                                        <div className="font-medium">
                                                            {new Date(cashIn.createdAt).toLocaleDateString('en-US', {
                                                                month: 'short',
                                                                day: 'numeric',
                                                                year: 'numeric'
                                                            })}
                                                        </div>
                                                        <div className="text-xs text-gray-500">
                                                            {new Date(cashIn.createdAt).toLocaleTimeString('en-US', {
                                                                hour: '2-digit',
                                                                minute: '2-digit'
                                                            })}
                                                        </div>
                                                    </div>
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap">
                                                    {editingTransactionId === cashIn._id ? (
                                                        <div className="flex items-center gap-2">
                                                            <select
                                                                defaultValue={cashIn.category || 'other_income'}
                                                                onChange={(e) => updateTransactionCategory(cashIn._id, e.target.value, 'in')}
                                                                disabled={updatingTransactionId === cashIn._id}
                                                                className="text-xs border border-green-300 rounded px-2 py-1 focus:outline-none focus:ring-2 focus:ring-green-500 bg-white"
                                                                autoFocus
                                                            >
                                                                {cashInCategories.map(cat => (
                                                                    <option key={cat.value} value={cat.value}>
                                                                        {cat.label}
                                                                    </option>
                                                                ))}
                                                            </select>
                                                            <button
                                                                onClick={(e) => {
                                                                    e.stopPropagation()
                                                                    setEditingTransactionId(null)
                                                                    setSelectedRowId(null)
                                                                }}
                                                                className="p-1 hover:bg-gray-100 rounded"
                                                                disabled={updatingTransactionId === cashIn._id}
                                                                title="Cancel (Esc)"
                                                            >
                                                                <X className="w-3 h-3 text-gray-500" />
                                                            </button>
                                                        </div>
                                                    ) : (
                                                        <div className="flex items-center gap-2">
                                                            <span className="px-3 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800 capitalize">
                                                                {cashInCategories.find(c => c.value === cashIn.category)?.label || cashIn.category || 'Other'}
                                                            </span>
                                                            <button
                                                                onClick={(e) => {
                                                                    e.stopPropagation()
                                                                    setEditingTransactionId(cashIn._id)
                                                                }}
                                                                className="p-1 hover:bg-green-100 rounded transition-colors"
                                                                title="Edit category (Enter)"
                                                            >
                                                                <Edit2 className="w-3 h-3 text-green-600" />
                                                            </button>
                                                        </div>
                                                    )}
                                                </td>
                                                <td className="px-6 py-4 text-sm text-gray-900">
                                                    <div className="max-w-xs truncate" title={cashIn.description}>
                                                        {isChangeReturnTransaction(cashIn.description) ? (
                                                            <span className="flex items-center gap-1">
                                                                <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-yellow-100 text-yellow-800 border border-yellow-300">
                                                                    💰 Change Returned
                                                                </span>
                                                            </span>
                                                        ) : (
                                                            cashIn.description || 'No description'
                                                        )}
                                                    </div>
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                                                    {cashIn.recordedBy?.name || 'N/A'}
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-right">
                                                    <div className="flex items-center justify-end gap-2">
                                                        <ArrowUpCircle className="w-4 h-4 text-green-500" />
                                                        <span className="text-sm font-bold text-green-600">
                                                            {formatCurrency(cashIn.totalAmount)}
                                                        </span>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                    <tfoot className="bg-gray-100">
                                        <tr>
                                            <td colSpan="5" className="px-6 py-4 text-right text-sm font-bold text-gray-900">
                                                Total Cash In:
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-right">
                                                <span className="text-lg font-bold text-green-600">
                                                    {formatCurrency(cashInStats.totalAmount)}
                                                </span>
                                            </td>
                                        </tr>
                                    </tfoot>
                                </table>
                                </div>
                            </>
                        ) : (
                            <div className="text-center py-12">
                                <div className="w-16 h-16 mx-auto mb-4 bg-gray-100 rounded-full flex items-center justify-center">
                                    <TrendingUp className="w-8 h-8 text-gray-400" />
                                </div>
                                <p className="text-gray-600 font-medium">No cash in transactions found</p>
                                <p className="text-sm text-gray-500 mt-1">for the selected period</p>
                            </div>
                        )}
                    </div>

                    {/* Expense Transactions Section */}
                    <div className="bg-white rounded-xl shadow-lg p-6 border border-gray-200 mt-6">
                        <div className="flex items-center justify-between mb-6">
                            <div className="flex items-center gap-3">
                                <div className="bg-red-100 rounded-full p-3">
                                    <TrendingDown className="w-6 h-6 text-red-600" />
                                </div>
                                <div>
                                    <div className="flex items-center gap-2">
                                        <h2 className="text-2xl font-bold text-gray-900">Expense Transactions</h2>
                                        {selectedExpenseCategory && (
                                            <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800 border border-red-300">
                                                Filtered: {selectedExpenseCategory}
                                                <button
                                                    onClick={() => setSelectedExpenseCategory(null)}
                                                    className="ml-1 hover:bg-red-200 rounded-full p-0.5"
                                                    title="Clear filter"
                                                >
                                                    <X className="w-3 h-3" />
                                                </button>
                                            </span>
                                        )}
                                    </div>
                                    <p className="text-sm text-gray-600">
                                        {selectedExpenseCategory
                                            ? `Showing ${selectedExpenseCategory} transactions only`
                                            : 'All cash out transactions in the selected period'
                                        }
                                    </p>
                                </div>
                            </div>
                            <div className="text-right">
                                <p className="text-sm text-gray-600">Total Expenses</p>
                                <p className="text-3xl font-bold text-red-600">{formatCurrency(expenseStats.totalAmount)}</p>
                                <p className="text-xs text-gray-500 mt-1">{expenseStats.totalExpenses} transactions</p>
                            </div>
                        </div>

                        {/* Expense Categories Summary */}
                        {Object.keys(expenseStats.byCategory).length > 0 && (
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                                {Object.entries(expenseStats.byCategory).map(([category, data]) => (
                                    <div
                                        key={category}
                                        onClick={() => {
                                            if (selectedExpenseCategory === category) {
                                                setSelectedExpenseCategory(null)
                                            } else {
                                                setSelectedExpenseCategory(category)
                                            }
                                        }}
                                        className={`bg-gradient-to-br from-red-50 to-pink-50 border rounded-lg p-4 cursor-pointer transition-all hover:shadow-md ${
                                            selectedExpenseCategory === category
                                                ? 'border-red-500 border-2 ring-2 ring-red-300 shadow-lg'
                                                : 'border-red-200'
                                        }`}
                                    >
                                        <div className="flex items-center justify-between mb-2">
                                            <h4 className="text-sm font-medium text-gray-700 capitalize">{category}</h4>
                                            <span className="bg-red-100 text-red-800 text-xs font-bold px-2 py-1 rounded">
                                                {data.count}
                                            </span>
                                        </div>
                                        <p className="text-2xl font-bold text-red-700">{formatCurrency(data.amount)}</p>
                                    </div>
                                ))}
                            </div>
                        )}

                        {/* Expense Transactions Table */}
                        {filteredExpenseData.length > 0 ? (
                            <>
                                <div className="mb-3 p-2 bg-blue-50 border border-blue-200 rounded-lg">
                                    <p className="text-xs text-blue-700">
                                        💡 <strong>Keyboard shortcuts:</strong> Use <kbd className="px-1.5 py-0.5 bg-white border border-blue-300 rounded text-blue-800 font-mono">↑</kbd> <kbd className="px-1.5 py-0.5 bg-white border border-blue-300 rounded text-blue-800 font-mono">↓</kbd> to navigate rows, <kbd className="px-1.5 py-0.5 bg-white border border-blue-300 rounded text-blue-800 font-mono">Enter</kbd> to edit category, <kbd className="px-1.5 py-0.5 bg-white border border-blue-300 rounded text-blue-800 font-mono">Esc</kbd> to cancel
                                    </p>
                                </div>
                                <div className="overflow-x-auto">
                                <table className="min-w-full divide-y divide-gray-200">
                                    <thead className="bg-gray-50">
                                        <tr>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                Status
                                            </th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                Date & Time
                                            </th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                Category
                                            </th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                Description
                                            </th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                Account To
                                            </th>
                                            <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                Amount
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody className="bg-white divide-y divide-gray-200">
                                        {filteredExpenseData.map((expense, index) => (
                                            <tr
                                                key={expense._id || index}
                                                data-transaction-id={expense._id}
                                                className={`hover:bg-red-50 cursor-pointer transition-colors ${selectedRowId === expense._id ? 'bg-red-100 ring-2 ring-red-400' : ''} ${isChangeReturnTransaction(expense.description) ? 'bg-yellow-50 border-l-4 border-yellow-400' : ''}`}
                                                tabIndex={0}
                                                onKeyDown={(e) => handleRowKeyPress(e, expense._id, 'out')}
                                                onClick={() => setSelectedRowId(expense._id)}
                                                onFocus={() => setSelectedRowId(expense._id)}
                                            >
                                                <td className="px-6 py-4 whitespace-nowrap">
                                                    {isChangeReturnTransaction(expense.description) ? (
                                                        <div className="flex items-center gap-1">
                                                            <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-bold bg-yellow-400 text-yellow-900 border-2 border-yellow-500 shadow-sm">
                                                                💰 Change
                                                            </span>
                                                        </div>
                                                    ) : (
                                                        <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-700">
                                                            Normal
                                                        </span>
                                                    )}
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                    <div>
                                                        <div className="font-medium">
                                                            {new Date(expense.createdAt).toLocaleDateString('en-US', {
                                                                month: 'short',
                                                                day: 'numeric',
                                                                year: 'numeric'
                                                            })}
                                                        </div>
                                                        <div className="text-xs text-gray-500">
                                                            {new Date(expense.createdAt).toLocaleTimeString('en-US', {
                                                                hour: '2-digit',
                                                                minute: '2-digit'
                                                            })}
                                                        </div>
                                                    </div>
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap">
                                                    {editingTransactionId === expense._id ? (
                                                        <div className="flex items-center gap-2">
                                                            <select
                                                                defaultValue={expense.category || 'other_expense'}
                                                                onChange={(e) => updateTransactionCategory(expense._id, e.target.value, 'out')}
                                                                disabled={updatingTransactionId === expense._id}
                                                                className="text-xs border border-red-300 rounded px-2 py-1 focus:outline-none focus:ring-2 focus:ring-red-500 bg-white"
                                                                autoFocus
                                                            >
                                                                {cashOutCategories.map(cat => (
                                                                    <option key={cat.value} value={cat.value}>
                                                                        {cat.label}
                                                                    </option>
                                                                ))}
                                                            </select>
                                                            <button
                                                                onClick={(e) => {
                                                                    e.stopPropagation()
                                                                    setEditingTransactionId(null)
                                                                    setSelectedRowId(null)
                                                                }}
                                                                className="p-1 hover:bg-gray-100 rounded"
                                                                disabled={updatingTransactionId === expense._id}
                                                                title="Cancel (Esc)"
                                                            >
                                                                <X className="w-3 h-3 text-gray-500" />
                                                            </button>
                                                        </div>
                                                    ) : (
                                                        <div className="flex items-center gap-2">
                                                            <span className="px-3 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-red-100 text-red-800 capitalize">
                                                                {cashOutCategories.find(c => c.value === expense.category)?.label || expense.category || 'Other'}
                                                            </span>
                                                            <button
                                                                onClick={(e) => {
                                                                    e.stopPropagation()
                                                                    setEditingTransactionId(expense._id)
                                                                }}
                                                                className="p-1 hover:bg-red-100 rounded transition-colors"
                                                                title="Edit category (Enter)"
                                                            >
                                                                <Edit2 className="w-3 h-3 text-red-600" />
                                                            </button>
                                                        </div>
                                                    )}
                                                </td>
                                                <td className="px-6 py-4 text-sm text-gray-900">
                                                    <div className="max-w-xs truncate" title={expense.description}>
                                                        {isChangeReturnTransaction(expense.description) ? (
                                                            <span className="flex items-center gap-1">
                                                                <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-yellow-100 text-yellow-800 border border-yellow-300">
                                                                    💰 Change Returned
                                                                </span>
                                                            </span>
                                                        ) : (
                                                            expense.description || 'No description'
                                                        )}
                                                    </div>
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                                                    {expense.inAccountTo?.name || 'N/A'}
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-right">
                                                    <div className="flex items-center justify-end gap-2">
                                                        <ArrowDownCircle className="w-4 h-4 text-red-500" />
                                                        <span className="text-sm font-bold text-red-600">
                                                            {formatCurrency(expense.totalAmount)}
                                                        </span>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                    <tfoot className="bg-gray-100">
                                        <tr>
                                            <td colSpan="5" className="px-6 py-4 text-right text-sm font-bold text-gray-900">
                                                Total Expenses:
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-right">
                                                <span className="text-lg font-bold text-red-600">
                                                    {formatCurrency(expenseStats.totalAmount)}
                                                </span>
                                            </td>
                                        </tr>
                                    </tfoot>
                                </table>
                                </div>
                            </>
                        ) : (
                            <div className="text-center py-12">
                                <div className="w-16 h-16 mx-auto mb-4 bg-gray-100 rounded-full flex items-center justify-center">
                                    <TrendingDown className="w-8 h-8 text-gray-400" />
                                </div>
                                <p className="text-gray-600 font-medium">No expense transactions found</p>
                                <p className="text-sm text-gray-500 mt-1">for the selected period</p>
                            </div>
                        )}
                    </div>

                    {/* Summary Comparison */}
                    <div className="bg-gradient-to-br from-indigo-50 to-purple-50 rounded-xl shadow-lg p-6 border-2 border-indigo-200 mt-6">
                        <h2 className="text-xl font-bold text-gray-900 mb-6 text-center">Financial Summary</h2>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
                            <div className="bg-white rounded-lg p-4 shadow-sm text-center">
                                <p className="text-xs text-gray-600 mb-2">Bill Revenue</p>
                                <p className="text-2xl font-bold text-blue-600">{formatCurrency(paymentStats.totalAmount)}</p>
                                <p className="text-xs text-gray-500 mt-1">{paymentStats.totalBills} bills</p>
                            </div>
                            <div className="bg-white rounded-lg p-4 shadow-sm text-center">
                                <p className="text-xs text-gray-600 mb-2">Cash In</p>
                                <p className="text-2xl font-bold text-green-600">{formatCurrency(cashInStats.totalAmount)}</p>
                                <p className="text-xs text-gray-500 mt-1">{cashInStats.totalTransactions} transactions</p>
                            </div>
                            <div className="bg-white rounded-lg p-4 shadow-sm text-center">
                                <p className="text-xs text-gray-600 mb-2">Total Income</p>
                                <p className="text-2xl font-bold text-green-700">{formatCurrency(paymentStats.totalAmount + cashInStats.totalAmount)}</p>
                                <p className="text-xs text-gray-500 mt-1">Revenue + Cash In</p>
                            </div>
                            <div className="bg-white rounded-lg p-4 shadow-sm text-center">
                                <p className="text-xs text-gray-600 mb-2">Cash Out</p>
                                <p className="text-2xl font-bold text-red-600">{formatCurrency(expenseStats.totalAmount)}</p>
                                <p className="text-xs text-gray-500 mt-1">{expenseStats.totalExpenses} expenses</p>
                            </div>
                            <div className="bg-gradient-to-br from-purple-100 to-indigo-100 rounded-lg p-4 shadow-md text-center border-2 border-purple-300">
                                <p className="text-xs text-purple-700 font-semibold mb-2">Net Cash Flow</p>
                                <p className={`text-2xl font-bold ${((paymentStats.totalAmount + cashInStats.totalAmount) - expenseStats.totalAmount) >= 0 ? 'text-green-700' : 'text-red-700'}`}>
                                    {formatCurrency((paymentStats.totalAmount + cashInStats.totalAmount) - expenseStats.totalAmount)}
                                </p>
                                <p className="text-xs text-purple-600 mt-1">
                                    {((paymentStats.totalAmount + cashInStats.totalAmount) - expenseStats.totalAmount) >= 0 ? '📈 Profit' : '📉 Loss'}
                                </p>
                            </div>
                        </div>

                        {/* Detailed Breakdown */}
                        <div className="bg-white rounded-lg p-4 shadow-sm">
                            <h3 className="text-sm font-bold text-gray-700 mb-3 text-center">Calculation Breakdown</h3>
                            <div className="space-y-2 text-sm">
                                <div className="flex justify-between items-center pb-2 border-b border-gray-200">
                                    <span className="text-gray-600">Bill Revenue:</span>
                                    <span className="font-semibold text-blue-600">+ {formatCurrency(paymentStats.totalAmount)}</span>
                                </div>
                                <div className="flex justify-between items-center pb-2 border-b border-gray-200">
                                    <span className="text-gray-600">Cash In (Deposits):</span>
                                    <span className="font-semibold text-green-600">+ {formatCurrency(cashInStats.totalAmount)}</span>
                                </div>
                                <div className="flex justify-between items-center pb-2 border-b-2 border-gray-300">
                                    <span className="text-gray-600">Cash Out (Expenses):</span>
                                    <span className="font-semibold text-red-600">- {formatCurrency(expenseStats.totalAmount)}</span>
                                </div>
                                <div className="flex justify-between items-center pt-2">
                                    <span className="font-bold text-gray-900">Net Cash Flow:</span>
                                    <span className={`font-bold text-lg ${((paymentStats.totalAmount + cashInStats.totalAmount) - expenseStats.totalAmount) >= 0 ? 'text-green-700' : 'text-red-700'}`}>
                                        {formatCurrency((paymentStats.totalAmount + cashInStats.totalAmount) - expenseStats.totalAmount)}
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>
                </>
            )}
        </div>
    )
}

export default PaymentDistributionReport
